import type { SlideImageSize, TrainingSlideDef } from '@hcc/types';
import { SlideBlocks } from './SlideBlocks';
import { SlideRichText } from './SlideRichText';
import type { WatchGate } from './videoGate';

const SIDE_WIDTHS: Record<SlideImageSize, string> = {
  small: 'sm:w-40',
  medium: 'sm:w-64',
  large: 'sm:w-96',
};

const BELOW_HEIGHTS: Record<SlideImageSize, string> = {
  small: 'max-h-40',
  medium: 'max-h-80',
  large: 'max-h-[32rem]',
};

/**
 * A content page: title, body markup (headings, lists, callouts, **bold**,
 * links) and an optional image whose placement and size the author controls.
 * Side layouts stack on narrow screens so tablets in portrait stay readable.
 */
export function ContentSlide({
  slide,
  watch,
}: {
  slide: TrainingSlideDef;
  watch?: WatchGate;
}) {
  // Block-based slides (the structured successor) take precedence over
  // the legacy body/image fields.
  if (slide.blocks && slide.blocks.length > 0) {
    return (
      <div className="space-y-4">
        {slide.title ? <h2 className="text-xl font-semibold text-fg">{slide.title}</h2> : null}
        <SlideBlocks blocks={slide.blocks} watch={watch} />
      </div>
    );
  }
  const layout = slide.image_layout ?? 'below';
  const size = slide.image_size ?? 'medium';
  const beside = slide.image_key && (layout === 'left' || layout === 'right');

  const image = slide.image_key ? (
    <figure className={beside ? `w-full shrink-0 ${SIDE_WIDTHS[size]}` : undefined}>
      <img
        src={slide.image_key}
        alt={slide.image_alt ?? ''}
        className={`rounded-token-sm border border-line/10 ${
          beside
            ? 'w-full'
            : layout === 'full'
              ? 'w-full'
              : `max-w-full ${BELOW_HEIGHTS[size]}`
        }`}
      />
      {slide.image_caption ? (
        <figcaption className="mt-1.5 text-xs text-fg-muted">{slide.image_caption}</figcaption>
      ) : null}
    </figure>
  ) : null;

  const body = slide.body ? <SlideRichText body={slide.body} /> : null;

  return (
    <div className="space-y-4">
      {slide.title ? <h2 className="text-xl font-semibold text-fg">{slide.title}</h2> : null}
      {beside ? (
        <div
          className={`flex flex-col gap-4 ${layout === 'left' ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}
        >
          {image}
          <div className="min-w-0 flex-1">{body}</div>
        </div>
      ) : (
        <>
          {layout === 'full' && image ? image : null}
          {body}
          {layout !== 'full' && image ? image : null}
        </>
      )}
    </div>
  );
}
